import {
  isRoadTunnel,
  isTaxiwayRoadTunnel,
  isTaxiwayTunnel,
  NETWORK_KIND_MASK,
  NetworkKind,
  roadTunnelAxes,
  roadTunnelLink,
} from './ground';

/*
 * The airport's vehicles' way over the network: roads, the motorway, service roads, and the
 * tunnels that take service roads under taxiways and roads. A tile is a node; a road with a
 * tunnel under it is two, the road on top (node t, driven like any road) and the tunnel below
 * (node size + t), which runs only one way, along the service road, and joins nothing but the
 * tunnel's ramps (and tunnels further on). So a vehicle cannot turn from the road down into the
 * tunnel, nor come up out of it onto the road. A public road's tunnel under a taxiway is road,
 * driven straight through (see roadTunnelLink).
 *
 * Steps come in a fixed order (north, east, south, west): the same routes on every machine.
 */

/** Directions of a step: north, east, south, west (dx, dy), and the axis each runs along. */
const STEP_X = [0, 1, 0, -1] as const;
const STEP_Y = [-1, 0, 1, 0] as const;
const STEP_AXIS = [1, 0, 1, 0] as const;

export class ServiceGraph {
  /** Tiles (nodes below that are tunnels under roads). */
  readonly size: number;
  /** Per road tunnel tile, the axis its tunnel runs along (0 along x, 1 along y); else -1. */
  private readonly axis: Int8Array;
  /** The public roads' tunnels under taxiways (see roadTunnelAxes). */
  private readonly roadAxes: Int8Array | null;

  constructor(
    private readonly network: Uint8Array,
    private readonly width: number,
    private readonly height: number,
  ) {
    this.size = width * height;
    this.axis = new Int8Array(this.size).fill(-1);
    for (let t = 0; t < this.size; t++) {
      if (isRoadTunnel(network[t] as number)) this.axis[t] = this.axisOf(t);
    }
    this.roadAxes = roadTunnelAxes(network, width, height);
  }

  /** The axis the tunnel under a road at tile t runs along (0 along x, 1 along y), or -1. */
  tunnelAxis(t: number): number {
    return this.axis[t] as number;
  }

  /** Whether a node is a tunnel under a road. */
  below(node: number): boolean {
    return node >= this.size;
  }

  /** The tile a node is on (or under). */
  tile(node: number): number {
    return node >= this.size ? node - this.size : node;
  }

  /** Whether the airport's vehicles drive on (or through) a tile. */
  drivable(t: number): boolean {
    const v = this.network[t] as number;
    const kind = v & NETWORK_KIND_MASK;
    return (
      kind === NetworkKind.Road ||
      kind === NetworkKind.Highway ||
      kind === NetworkKind.Service ||
      isTaxiwayTunnel(v) ||
      isTaxiwayRoadTunnel(v)
    );
  }

  /** Whether a node is service road pace (a service road or a tunnel) rather than road pace. */
  serviceRoad(node: number): boolean {
    if (node >= this.size) return true;
    const v = this.network[node] as number;
    return (v & NETWORK_KIND_MASK) === NetworkKind.Service || isTaxiwayTunnel(v);
  }

  /** The nodes one step on from a node, into `out` (emptied first). */
  next(node: number, out: number[]): void {
    out.length = 0;
    const w = this.width;
    const under = node >= this.size;
    const t = under ? node - this.size : node;
    const x = t % w;
    const y = (t - x) / w;
    const ramp = (u: number) => {
      const v = this.network[u] as number;
      return (v & NETWORK_KIND_MASK) === NetworkKind.Service || isTaxiwayTunnel(v);
    };
    for (let d = 0; d < 4; d++) {
      const nx = x + (STEP_X[d] as number);
      const ny = y + (STEP_Y[d] as number);
      if (nx < 0 || ny < 0 || nx >= w || ny >= this.height) continue;
      const u = ny * w + nx;
      const along = STEP_AXIS[d] as number;
      if (under) {
        // In a tunnel under a road: on along it only, to the next tunnel tile or its ramp.
        if (this.axis[t] !== along) continue;
        if (isRoadTunnel(this.network[u] as number)) {
          if (this.axis[u] === along) out.push(this.size + u);
        } else if (ramp(u)) {
          out.push(u);
        }
        continue;
      }
      if (!this.drivable(u) || !roadTunnelLink(this.roadAxes, t, u, along)) continue;
      if (isRoadTunnel(this.network[u] as number)) {
        // From its ramp (or a taxiway's tunnel) along the way it runs: down into the tunnel.
        // Anything else is the road on top.
        out.push(this.axis[u] === along && ramp(t) ? this.size + u : u);
        continue;
      }
      // No way from the road on top down onto the ramp beside it.
      if (this.axis[t] === along && ramp(u)) continue;
      out.push(u);
    }
  }

  /**
   * The axis a road tunnel at t runs along: the one on which the tunnel tiles lead out to a
   * service road (or a taxiway's tunnel), at either end; -1 if neither does.
   */
  private axisOf(t: number): number {
    const w = this.width;
    const x0 = t % w;
    const y0 = (t - x0) / w;
    for (const along of [1, 0]) {
      for (const s of [-1, 1]) {
        let x = x0;
        let y = y0;
        for (;;) {
          x += along === 0 ? s : 0;
          y += along === 1 ? s : 0;
          if (x < 0 || y < 0 || x >= w || y >= this.height) break;
          const v = this.network[y * w + x] as number;
          if (isRoadTunnel(v)) continue;
          if ((v & NETWORK_KIND_MASK) === NetworkKind.Service || isTaxiwayTunnel(v)) return along;
          break;
        }
      }
    }
    return -1;
  }
}
